import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import StateContext from "../../context/StateContext";
import ModalNotification from "../../move2FComponents/ModalNotification";
import SaveButton from "../../move2FComponents/SaveButton";
import SetupChecklist from "../../move2FComponents/setupChecklist/SetupChecklist";
import { ReactComponent as ErrorIcon } from "../../img/ErrorIcon.svg";

import { sendEvent } from "../../services/analytics";
import { updateSegmentInData } from "../../services/dataService";
import { cleanName, createSegment, updateSegment } from "../../services/globalService";
import { StyledSmallError } from "../style/global.style";
import SmallLoader from "../utils/SmallLoader";
import { captureException } from "services/sentry";

const createNewSegment = async (displayName, mail) => {
	const res = await createSegment({ mail, name: displayName });
	if (res?.data) {
		const newSegCode = res.data?.code;
		sendEvent({ category: "segment", action: displayName, label: newSegCode });
		return res;
	}
};
const saveName = async (code, data) => {
	return await updateSegment({ id: code, data });
};

const EditSegment = () => {
	const { websiteURL, selectedSegmentCode, segments, segMap, user, showSpinner, dispatch, ACTIONS } = useContext(StateContext);
	const [errorMessage, setErrorMessage] = useState("");
	const { code } = useParams();
	const location = useLocation();
	const history = useHistory();

	useEffect(() => {
		let didCancel = false;
		const handleSegmentName = async () => {
			if (websiteURL) {
				const name = cleanName(websiteURL);
				const shouldCreateSegment = !code && location.pathname.includes("segments/create");
				const shouldSaveName = code && location.pathname.includes("segments/edit");
				try {
					let res = {};
					let payload = {};
					dispatch({ type: ACTIONS.SET_SPINNER, payload: true });
					if (shouldCreateSegment) {
						res = await createNewSegment(name, user?.email);
					} else if (shouldSaveName) {
						const data = { display: name, install_data: { ...segMap[code].install_data, display: name } };
						res = await saveName(code, data);
					}
					if (res?.data) {
						const newSegment = res.data;
						const { newSegments, newMap } = updateSegmentInData(newSegment, segments, segMap);
						payload = { segments: newSegments, haveSegments: true, segMap: newMap, websiteURL: "", selectedSegmentCode: newSegment.code, showSpinner: false };
						setErrorMessage("");
						if (!didCancel) {
							dispatch({ type: ACTIONS.SET_STATE, payload });
							if (shouldCreateSegment) {
								history.push(`/segments/edit/${newSegment.code}`);
							}
						}
					}
				} catch (e) {
					const payload = { websiteURL: "", selectedSegmentCode: null, showSpinner: false };
					if (e?.status === 402) {
						setErrorMessage(402);
						captureException(e, "log", {}, { action: "handleSegmentName", description: "failed due to payment required" }, { scope: "EditSegment.js" });
					} else {
						setErrorMessage(e?.message || "There was a problem, please try again later");
						captureException(e, "error", {}, { action: "handleSegmentName", description: "failed while trying to change segment" }, { scope: "EditSegment.js" });
					}
					if (!didCancel) {
						dispatch({ type: ACTIONS.SET_STATE, payload });
						console.error("CreateSegment.js ~ saveName ~ error", e);
					}
				}
			}
		};
		handleSegmentName();
		return () => {
			dispatch({ type: ACTIONS.SET_WEBSITE, payload: "" });
			return (didCancel = true);
		};
	}, []);

	const errorModal = () => {
		if (errorMessage === 402) {
			return (
				<ModalNotification
					show={errorMessage}
					setShow={setErrorMessage}
					close={() => {
						setErrorMessage("");
					}}
					title="Plan exceeded">
					<p>Your Fixel plan has reached it's max segments. If you require additional segments, please upgrade your plan.</p>
					<SaveButton type="button" label="Upgrade" onClick={() => history.push("/plans")} alignTo="left" />
				</ModalNotification>
			);
		} else {
			return (
				<StyledSmallError>
					<ErrorIcon />
					{errorMessage}
				</StyledSmallError>
			);
		}
	};
	const onClick = useCallback(
		(e) => {
			const pageName = e.target.value;
			let url = `${location.pathname}/${pageName}`;
			history.push(url);
		},
		[history, location.pathname]
	);

	const isFBDone = useMemo(() => code && segMap && segMap[code]?.install_data?.options?.fb, [code, segMap]);
	const isGoogleDone = useMemo(() => code && segMap && segMap[code]?.install_data?.options?.gapi, [code, segMap]);
	const isPlatformsDone = useMemo(() => {
		if (!segMap || !code) return false;
		const someTrue =
			segMap[code]?.install_data?.options &&
			Object.entries(segMap[code]?.install_data?.options).some( ([key, value]) => (key === "tb" || key === "pt" || key === "ob" || key === "ms") && value)
		return someTrue;
	}, [code, segMap]);
	const list = useMemo(
		() => [
			{
				label: "Connect with Facebook Ads",
				getButtonLabel: () => (isFBDone ? "Edit" : "Connect"),
				buttonValue: "facebook",
				onClick,
				getIsChecked: () => isFBDone,
				getIsDisabled: () => !code
			},
			{
				label: "Connect with Google Analytics",
				getButtonLabel: () => (isGoogleDone ? "Edit" : "Connect"),
				buttonValue: "google",
				onClick,
				getIsChecked: () => isGoogleDone,
				getIsDisabled: () => !code
			},
			{
				label: "Connect with other platforms",
				getButtonLabel: () => (isPlatformsDone ? "Edit" : "Connect"),
				buttonValue: "platforms",
				onClick,
				getIsChecked: () => isPlatformsDone,
				getIsDisabled: () => !code
			}
		],
		[code, isFBDone, isPlatformsDone, onClick]
	);

	const goToInstall = () => {
		history.push(`/segments/install/${selectedSegmentCode}`);
	};
	const innerText = useMemo(() => {
		if (code) {
			return "You can edit your segment’s name and connected ad platforms";
		} else {
			return "First, set your segment’s name and then select which ad platforms to connect";
		}
	}, [code]);

	return (
		<>
			<SetupChecklist
				title="Let’s Get You Set Up"
				innerText={innerText}
				list={list}
				onDone={goToInstall}
				doneButtonDisabled={!code}
				doneButtonLabel={showSpinner ? <SmallLoader inline={true} /> : "Save"}
			/>
			{errorMessage ? errorModal() : null}
		</>
	);
};
export default EditSegment;
